import { useEffect, useState } from "react";
import { Switch, message, Avatar, Tag, Radio } from "antd";
import { FiUser, FiShield, FiBell, FiMoon, FiSun, FiLogOut, FiPhone, FiMail, FiMapPin, FiLock } from "react-icons/fi";
import MainLayout from "../layout/MainLayout";
import { useAuth } from "../api/useAuth";
import { useTheme } from "../context/ThemeContext";
import { goToLogout } from "../api/client";
import { securityApi } from "../api/endpoints";
import TransactionPinModal from "../Components/modals/TransactionPinModal";
import SecurityOtpConfirmModal from "../Components/modals/SecurityOtpConfirmModal";

const Section = ({ icon, title, children }) => (
  <div
    className="settings-section rounded-4 mb-4"
    style={{
      border: "1px solid var(--border-color)",
      background: "var(--card-bg)",
      overflow: "hidden",
    }}
  >
    <div
      className="settings-section-header d-flex align-items-center gap-2 px-4 py-3"
      style={{ borderBottom: "1px solid var(--border-color)" }}
    >
      <span style={{ color: "var(--accent-color)" }}>{icon}</span>
      <h2 className="fs-6 fw-semibold m-0">{title}</h2>
    </div>
    <div className="px-4 py-2">{children}</div>
  </div>
);

const Row = ({ label, description, children, last }) => (
  <div
    className="settings-row d-flex justify-content-between align-items-center gap-3 py-3"
    style={{ borderBottom: last ? "none" : "1px solid var(--border-color)" }}
  >
    <div>
      <div className="fw-medium" style={{ fontSize: "14px" }}>{label}</div>
      {description && (
        <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>
          {description}
        </div>
      )}
    </div>
    <div style={{ flexShrink: 0 }}>{children}</div>
  </div>
);

const KycBadge = ({ status }) => {
  const map = {
    VERIFIED: { color: "success", text: "Verified ✓" },
    PENDING: { color: "warning", text: "Pending" },
    REJECTED: { color: "error", text: "Rejected" },
  };
  const s = map[status] || map.PENDING;
  return <Tag color={s.color} style={{ fontWeight: 600, fontSize: "12px" }}>{s.text}</Tag>;
};

const Settings = () => {
  const { user, loading } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const [notif, setNotif] = useState({
    email: true,
    push: true,
    sms: true,
    marketing: false,
  });

  // Transaction verification preference: OTP (email) / PIN / BOTH.
  const [security, setSecurity] = useState({ mode: "OTP", pinSet: false });
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [removePinOpen, setRemovePinOpen] = useState(false);
  const [pendingMode, setPendingMode] = useState(null);

  useEffect(() => {
    securityApi
      .get()
      .then(setSecurity)
      .catch(() => {});
  }, []);

  const handleModeChange = (nextMode) => {
    if (nextMode === security.mode) return;
    if (nextMode !== "OTP" && !security.pinSet) {
      message.warning("Set a transaction PIN first.");
      return;
    }
    setPendingMode(nextMode);
  };

  const confirmModeChange = async (otpCode) => {
    const res = await securityApi.setMode(otpCode, pendingMode);
    setSecurity((s) => ({ ...s, mode: res.mode }));
    setPendingMode(null);
    message.success("Transfer verification method updated");
  };

  const confirmRemovePin = async (otpCode) => {
    await securityApi.removePin(otpCode);
    setSecurity({ mode: "OTP", pinSet: false });
    setRemovePinOpen(false);
    message.success("PIN removed, transfers now use email OTP");
  };

  const fullName = user
    ? [user.firstName, user.lastName].filter(Boolean).join(" ").trim() || user.displayName || "-"
    : "-";

  const initials = fullName
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <MainLayout
      expand={false}
      NavName="Settings"
      pageClassName="dashboard-page settings-page"
      contentClassName="dashboard-content"
    >
      <div className="settings-shell">
        <div className="settings-overview">
          <div>
            <span className="dashboard-eyebrow">Preferences</span>
            {/* No <h2>Settings</h2> here, the navbar already titles the page,
                so repeating it printed "Settings" twice down the screen. */}
            <p>Manage profile details, appearance, notifications, security, and verification from one workspace.</p>
          </div>
        </div>

        {/* Profile Card */}
        <div
          className="settings-profile-card rounded-4 p-4 mb-4 d-flex align-items-center gap-4"
          style={{ background: "var(--card-bg)", border: "1px solid var(--border-color)" }}
        >
          <Avatar
            size={72}
            style={{ background: "linear-gradient(135deg, #1bbb6b, #0d9e58)", fontWeight: 700, fontSize: "24px", flexShrink: 0 }}
            src={user?.avatarUrl || undefined}
          >
            {!user?.avatarUrl && (loading ? "…" : initials)}
          </Avatar>
          <div className="d-flex flex-column gap-1">
            <span className="fw-bold fs-5">{loading ? "Loading…" : fullName}</span>
            <span style={{ color: "var(--text-muted)", fontSize: "13px" }}>{user?.email || "-"}</span>
            <div className="d-flex gap-2 mt-1 flex-wrap">
              <Tag color="blue" style={{ textTransform: "capitalize" }}>
                {user?.accountType || "personal"}
              </Tag>
              <KycBadge status={user?.kycStatus} />
              {user?.isAdmin && <Tag color="green">Admin</Tag>}
            </div>
          </div>
        </div>

        {/* Profile Details */}
        <Section icon={<FiUser size={16} />} title="Profile Information">
          <Row label="Full Name">
            <span style={{ fontSize: "14px" }}>{loading ? "-" : fullName}</span>
          </Row>
          <Row label="Email" description="Managed by ARM Financial onboarding">
            <span style={{ fontSize: "14px", display: "flex", alignItems: "center", gap: "6px" }}>
              <FiMail size={14} style={{ color: "var(--text-muted)" }} />
              {user?.email || "-"}
            </span>
          </Row>
          <Row label="Mobile">
            <span style={{ fontSize: "14px", display: "flex", alignItems: "center", gap: "6px" }}>
              <FiPhone size={14} style={{ color: "var(--text-muted)" }} />
              {user?.mobile || "-"}
            </span>
          </Row>
          <Row label="Country" last>
            <span style={{ fontSize: "14px", display: "flex", alignItems: "center", gap: "6px" }}>
              <FiMapPin size={14} style={{ color: "var(--text-muted)" }} />
              {user?.countryCode || "-"}
            </span>
          </Row>
        </Section>

        {/* Appearance */}
        {/* <Section icon={theme === "dark" ? <FiMoon size={16} /> : <FiSun size={16} />} title="Appearance">
          <Row label="Dark Mode" description="Switch between light and dark themes" last>
            <Switch
              checked={theme === "dark"}
              onChange={toggleTheme}
              checkedChildren="🌙"
              unCheckedChildren="☀️"
            />
          </Row>
        </Section> */}

        {/* Notifications */}
        <Section icon={<FiBell size={16} />} title="Notifications">
          <Row label="Email Notifications" description="Transaction confirmations and account alerts">
            <Switch checked={notif.email} onChange={(v) => { setNotif((p) => ({ ...p, email: v })); message.success(`Email notifications ${v ? "on" : "off"}`); }} />
          </Row>
          <Row label="Push Notifications" description="Real-time alerts on your devices">
            <Switch checked={notif.push} onChange={(v) => { setNotif((p) => ({ ...p, push: v })); message.success(`Push notifications ${v ? "on" : "off"}`); }} />
          </Row>
          <Row label="SMS Alerts" description="OTP and transaction SMS messages">
            <Switch checked={notif.sms} onChange={(v) => { setNotif((p) => ({ ...p, sms: v })); message.success(`SMS alerts ${v ? "on" : "off"}`); }} />
          </Row>
          <Row label="Marketing Emails" description="Product updates and promotional offers" last>
            <Switch checked={notif.marketing} onChange={(v) => { setNotif((p) => ({ ...p, marketing: v })); message.success(`Marketing emails ${v ? "on" : "off"}`); }} />
          </Row>
        </Section>

        {/* Security */}
        <Section icon={<FiShield size={16} />} title="Security">
          <Row label="Password" description="Managed via ARM Financial onboarding">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => message.info("Visit ARM Financial onboarding to change your password.")}
              style={{ fontSize: "12px", borderRadius: "8px" }}
            >
              Manage
            </button>
          </Row>
          <Row label="Two-Factor Authentication" description="Add an extra layer of security to your account">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => message.info("2FA is managed by ARM Financial onboarding.")}
              style={{ fontSize: "12px", borderRadius: "8px" }}
            >
              Configure
            </button>
          </Row>
          <Row label="Active Sessions" description="You are signed in on this device" last>
            <button
              className="btn btn-sm"
              onClick={goToLogout}
              style={{ fontSize: "12px", borderRadius: "8px", background: "#FC555B", color: "#fff", border: "none", display: "flex", alignItems: "center", gap: "6px" }}
            >
              <FiLogOut size={13} /> Sign out all
            </button>
          </Row>
        </Section>

        {/* Transaction Verification */}
        <Section icon={<FiLock size={16} />} title="Transaction Verification">
          <Row
            label="Verification method"
            description="How you confirm an outbound transfer before it's sent"
          >
            <Radio.Group
              value={security.mode}
              onChange={(e) => handleModeChange(e.target.value)}
              optionType="button"
              buttonStyle="solid"
              size="small"
            >
              <Radio.Button value="OTP">Email OTP</Radio.Button>
              <Radio.Button value="PIN">PIN</Radio.Button>
              <Radio.Button value="BOTH">Both</Radio.Button>
            </Radio.Group>
          </Row>
          <Row
            label="Transaction PIN"
            description={security.pinSet ? "A 6-digit PIN is set on your account" : "Not set, required to use PIN or Both"}
            last
          >
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setPinModalOpen(true)}
                style={{ fontSize: "12px", borderRadius: "8px" }}
              >
                {security.pinSet ? "Change PIN" : "Set PIN"}
              </button>
              {security.pinSet && (
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setRemovePinOpen(true)}
                  style={{ fontSize: "12px", borderRadius: "8px", color: "#FC555B", borderColor: "#FC555B" }}
                >
                  Remove
                </button>
              )}
            </div>
          </Row>
        </Section>

      </div>

      <TransactionPinModal
        open={pinModalOpen}
        hasPin={security.pinSet}
        onClose={() => setPinModalOpen(false)}
        onSaved={() => securityApi.get().then(setSecurity).catch(() => {})}
      />
      <SecurityOtpConfirmModal
        open={!!pendingMode}
        title="Confirm verification method"
        description={`Switch to ${pendingMode === "OTP" ? "Email OTP" : pendingMode === "PIN" ? "PIN" : "Both OTP and PIN"} for transfers?`}
        confirmLabel="Switch"
        onClose={() => setPendingMode(null)}
        onConfirm={confirmModeChange}
      />
      <SecurityOtpConfirmModal
        open={removePinOpen}
        title="Remove transaction PIN"
        description="Transfers will fall back to email OTP verification."
        confirmLabel="Remove PIN"
        onClose={() => setRemovePinOpen(false)}
        onConfirm={confirmRemovePin}
      />
    </MainLayout>
  );
};

export default Settings;
